import {Check, Loader2, MapPin, Plus, Trash2} from "lucide-react";
import {useState} from "react";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ConfirmationModal from "@/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {CoordinatesRequiredGate} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {
  prewarmCoordinatesForGate,
  prewarmGateCoordinatesOnPointerDown,
  useComplianceGateCoordinates,
} from "@/src/modules/core/hooks/use-compliance-gate-coordinates";
import {formatTime, moment} from "@/src/modules/core/lib";
import {resolveDeviceGpsForSubmit} from "@/src/modules/core/lib/resolve-device-gps-for-submit";
import {
  googleMapsDirectionsUrl,
  openExternalUrl,
  parseGpsLatLng,
  wazeNavigateUrl,
} from "@/src/modules/officer/lib/external-navigation";
import {useCheckPatrolVisit} from "@/src/modules/shifts/hooks/use-check-patrol-visit";
import {useDeletePatrolVisit} from "@/src/modules/shifts/hooks/use-delete-patrol-visit";
import type {ShiftPatrolVisit} from "@/src/modules/shifts/types";

import {AddPatrolStopDialog} from "./add-patrol-stop-dialog";

const NO_LOCATION_TOAST = "No location for this property";

function PatrolVisitNavigateButton({
  propertyName,
  gpsCoordinates,
  onMissingLocation,
}: {
  propertyName: string;
  gpsCoordinates: string | null | undefined;
  onMissingLocation: () => void;
}) {
  const latLng = parseGpsLatLng(gpsCoordinates);

  if (!latLng) {
    return (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="text-muted-foreground size-8 opacity-50"
        aria-label={`Navigate to ${propertyName} (no location)`}
        onClick={onMissingLocation}
      >
        <MapPin className="size-4" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label={`Navigate to ${propertyName}`}
          />
        }
      >
        <MapPin className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => openExternalUrl(googleMapsDirectionsUrl(latLng))}>
          Google Maps
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openExternalUrl(wazeNavigateUrl(latLng))}>
          Waze
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatSuggestedWindow(visit: ShiftPatrolVisit): string | null {
  if (!visit.suggested_start_at && !visit.suggested_end_at) return null;
  const start = visit.suggested_start_at ? formatTime(visit.suggested_start_at) : "—";
  const end = visit.suggested_end_at ? formatTime(visit.suggested_end_at) : "—";
  return `${start} – ${end}`;
}

type PatrolCheckpointListProps = {
  shiftId: number;
  visits: ShiftPatrolVisit[];
};

export function PatrolCheckpointList({shiftId, visits}: PatrolCheckpointListProps) {
  const toast = useToast();
  const {submitBlockedByLocation} = useComplianceGateCoordinates(true);
  const {mutateAsync: checkVisit, isPending: isChecking} = useCheckPatrolVisit();
  const {mutateAsync: deleteVisit, isPending: isDeleting} = useDeletePatrolVisit();
  const [checkingVisitId, setCheckingVisitId] = useState<number | null>(null);
  const [deleteVisitId, setDeleteVisitId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);

  const ordered = [...visits].sort((a, b) => a.sort_order - b.sort_order);
  const checkedCount = ordered.filter((v) => v.checked).length;
  const progressLabel =
    ordered.length === 0
      ? "No stops yet"
      : `${checkedCount} of ${ordered.length} checked`;

  const handleCheck = async (visit: ShiftPatrolVisit) => {
    if (visit.checked || submitBlockedByLocation) return;

    setGpsBusy(true);
    setCheckingVisitId(visit.shift_patrol_visit_id);
    try {
      const gps = await resolveDeviceGpsForSubmit({
        onFailure: "toast",
        captureContext: {feature: "patrol-checkpoint", operation: "check"},
      });
      if (!gps) return;

      await checkVisit({
        shiftId,
        visitId: visit.shift_patrol_visit_id,
        data: {
          gps_coordinates: gps.gps_coordinates,
          gps_source: "device",
        },
      });
      toast.success("Checkpoint recorded");
    } catch {
      // errorHandler (already-checked → toast + invalidate on next open)
    } finally {
      setGpsBusy(false);
      setCheckingVisitId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteVisitId == null) return;
    try {
      await deleteVisit({shiftId, visitId: deleteVisitId});
      toast.success("Stop removed");
      setDeleteVisitId(null);
    } catch {
      // errorHandler
    }
  };

  return (
    <Card className="min-h-0 shrink-0">
      <CardHeader>
        <CardTitle>Checkpoints</CardTitle>
        <CardDescription>{progressLabel}</CardDescription>
        <CardAction>
          <Button type="button" size="sm" variant="outline" onClick={() => setAddOpen(true)}>
            <Plus className="size-4" />
            Add stop
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-3">
        {submitBlockedByLocation ? <CoordinatesRequiredGate /> : null}

        {ordered.length === 0 ? (
          <p className="text-muted-foreground text-sm">No checkpoints yet. Add a stop to begin.</p>
        ) : (
          <div
            className="border-border max-h-[min(28rem,55vh)] min-h-0 overflow-y-auto overscroll-contain rounded-md border p-2"
            role="region"
            aria-label="Checkpoint list"
          >
            <ul className="flex flex-col gap-2">
              {ordered.map((visit) => {
                const windowLabel = formatSuggestedWindow(visit);
                const isThisChecking =
                  checkingVisitId === visit.shift_patrol_visit_id && (isChecking || gpsBusy);
                const canDelete = visit.source === "manual" && !visit.checked;

                return (
                  <li
                    key={visit.shift_patrol_visit_id}
                    className="border-border flex flex-col gap-2 rounded-md border bg-card p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-foreground">{visit.property.name}</p>
                          <PatrolVisitNavigateButton
                            propertyName={visit.property.name}
                            gpsCoordinates={visit.property.gps_coordinates}
                            onMissingLocation={() => toast.error(NO_LOCATION_TOAST)}
                          />
                        </div>
                        {visit.source === "manual" ? (
                          <Badge variant="secondary" className="mt-1">
                            Manual
                          </Badge>
                        ) : null}
                        {windowLabel ? (
                          <p className="text-muted-foreground mt-0.5 text-xs">
                            Suggested visit {windowLabel}
                          </p>
                        ) : null}
                        {visit.duties ? (
                          <p className="text-muted-foreground mt-1 text-xs whitespace-pre-wrap">
                            Duties: {visit.duties}
                          </p>
                        ) : null}
                        {visit.checked && visit.checked_at ? (
                          <p className="text-muted-foreground mt-1 text-xs">
                            Checked {formatTime(visit.checked_at)}
                            {moment(visit.checked_at).isSame(moment(), "day")
                              ? ""
                              : ` · ${moment(visit.checked_at).format("MMM D")}`}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-start gap-1">
                        {visit.checked ? <Badge variant="success">Checked</Badge> : null}
                        {canDelete ? (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="text-destructive size-8"
                            aria-label={`Remove stop ${visit.property.name}`}
                            disabled={isDeleting}
                            onClick={() => setDeleteVisitId(visit.shift_patrol_visit_id)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        ) : null}
                        {!visit.checked ? (
                          <Button
                            type="button"
                            size="sm"
                            disabled={submitBlockedByLocation || isThisChecking}
                            {...prewarmGateCoordinatesOnPointerDown()}
                            onPointerDown={() => prewarmCoordinatesForGate()}
                            onClick={() => handleCheck(visit)}
                          >
                            {isThisChecking ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Check className="size-4" />
                            )}
                            Check
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>

      <AddPatrolStopDialog open={addOpen} shiftId={shiftId} onClose={() => setAddOpen(false)} />

      {deleteVisitId != null ? (
        <ConfirmationModal
          open
          loading={isDeleting}
          title="Remove stop"
          text="Remove this manual stop from your checkpoint list?"
          confirmButtonLabel="Remove"
          confirmButtonClassName="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          onClose={() => setDeleteVisitId(null)}
          onConfirm={handleDeleteConfirm}
        />
      ) : null}
    </Card>
  );
}
